import { Object3D, Vector3 } from 'three';
import { IEnemy } from '../enemy/IEnemy';

const LOCK_RANGE = 2000;
const LOCK_ANGLE = Math.PI / 3; // 前方60度
const LOCK_TIME  = 1.5;         // 秒

export class LockOnSystem {
  private currentTarget: IEnemy | null = null;
  private lockTimer = 0;
  private tabWasDown = false;

  update(player: Object3D, enemies: IEnemy[], tabDown: boolean, delta: number): IEnemy | null {
    // Tabキーでターゲット切り替え
    if (tabDown && !this.tabWasDown) {
      this.switchTarget(player, enemies);
    }
    this.tabWasDown = tabDown;

    // ターゲットが死んだらリセット
    if (this.currentTarget && !this.currentTarget.isAlive) {
      this.currentTarget = null;
      this.lockTimer = 0;
    }

    // ターゲットが射程外に出たらリセット
    if (this.currentTarget) {
      const dist = player.position.distanceTo(this.currentTarget.position);
      if (dist > LOCK_RANGE) {
        this.currentTarget = null;
        this.lockTimer = 0;
      }
    }

    // ターゲットがなければ自動選択（距離のみ、角度は問わない）
    if (!this.currentTarget) {
      this.currentTarget = this.findNearest(player, enemies);
      this.lockTimer = 0;
    }

    // ロックオンタイマー（前方にいるときだけ進行）
    if (this.currentTarget) {
      const inFront = this.isInFront(player, this.currentTarget);
      if (inFront) {
        this.lockTimer = Math.min(this.lockTimer + delta, LOCK_TIME);
      } else {
        this.lockTimer = Math.max(this.lockTimer - delta * 2, 0);
      }
    }

    return this.lockTimer >= LOCK_TIME ? this.currentTarget : null;
  }

  private findNearest(player: Object3D, enemies: IEnemy[]): IEnemy | null {
    let nearest: IEnemy | null = null;
    let minDist = Infinity;
    for (const e of enemies) {
      if (!e.isAlive) continue;
      const d = player.position.distanceTo(e.position);
      if (d < LOCK_RANGE && d < minDist) {
        minDist = d;
        nearest = e;
      }
    }
    return nearest;
  }

  private switchTarget(player: Object3D, enemies: IEnemy[]): void {
    const inRange = enemies.filter(e =>
      e.isAlive && player.position.distanceTo(e.position) < LOCK_RANGE
    );
    if (inRange.length === 0) return;
    const idx = this.currentTarget ? inRange.indexOf(this.currentTarget) : -1;
    this.currentTarget = inRange[(idx + 1) % inRange.length];
    this.lockTimer = 0;
  }

  private isInFront(player: Object3D, enemy: IEnemy): boolean {
    const forward = new Vector3(0, 0, -1).applyQuaternion(player.quaternion);
    const toEnemy = enemy.position.clone().sub(player.position);
    const dist = toEnemy.length();
    if (dist > LOCK_RANGE) return false;
    toEnemy.normalize();
    return forward.dot(toEnemy) > Math.cos(LOCK_ANGLE);
  }

  get lockProgress(): number {
    return this.lockTimer / LOCK_TIME;
  }

  getTarget(): IEnemy | null {
    return this.currentTarget;
  }
}
